<!-- Overrides @basecom-gmbh/pond/components/product/ProductCard.vue -->
<script setup lang="ts">
import type { Schemas } from '@shopware/api-client/api-types';
import { getProductRoute, getTranslatedProperty } from '@shopware-pwa/helpers-next';

const props = withDefaults(
    defineProps<{
        product: Schemas['Product'];
        layout?: 'standard' | 'minimal' | 'image';
        displayMode?: 'standard' | 'cover' | 'contain';
    }>(),
    {
        layout: 'standard',
        displayMode: 'cover',
    },
);

const emit = defineEmits(['select-product', 'view-product']);

const { t } = useI18n();
const { getProductCover } = useMedia();
const productCard = ref(null);
const cover = getProductCover(props.product.cover);
const configStore = useConfigStore();
const wishlistEnabled = configStore.get('core.cart.wishlistEnabled');

const route = useRoute();
const { isAddToCartAvailable } = useAddToCartAvailability(props.product);


const { stop } = useIntersectionObserver(productCard, ([{ isIntersecting }]) => {
    if (isIntersecting) {
        emit('view-product');
        stop();
    }
});

const quantityAndPackage = computed(() => {
    const quantityProperty = props.product.properties?.find(
        property => property.group?.customFields?.propertyIsQuantityProperty,
    );
    if (!quantityProperty) return null;
    return `${getTranslatedProperty(quantityProperty?.group, 'name')}: ${getTranslatedProperty(quantityProperty, 'name')}`;
});

const moreVariantsLabel = computed(() => {
    const siblingCount = props.product?.extensions?.productExtension?.siblingsCount;

    if (siblingCount && props.product.options) {
        return `${t('product.variationsLabel', {
            variationNumbers: props.product?.extensions?.productExtension?.siblingsCount,
        })} ${props.product.options.map(option => (option?.group?.customFields?.propertyPluralTranslation && siblingCount > 1 ? option?.group?.customFields?.propertyPluralTranslation : getTranslatedProperty(option.group, 'name'))).join('/')}`;
    }
    return null;
});
</script>

<template>
    <!-- IDS-84: Add base styles for product card-->
    <div
        v-if="props.product"
        ref="productCard"
        class="relative flex w-full flex-col justify-between p-0"
    >
        <div
            v-if="wishlistEnabled"
            class="absolute right-0 top-0 z-10"
        >
            <ProductAddToWishlist :product="props.product" />
        </div>

        <LocaleLink
            :to="getProductRoute(product)"
            class="group"
            @click="$emit('select-product')"
        >
            <div class="flex flex-col">
                <!-- CUSTOM IDS-95: added border styling -->
                <div
                    class="relative z-0 aspect-square overflow-visible border border-gray-extralight"
                    :class="{
                        'rounded-lg': layout !== 'minimal',
                    }"
                >
                    <div class="absolute left-0 top-0 z-50 flex flex-col gap-1">
                        <div
                            v-if="product.calculatedPrice.listPrice"
                            class="text-xs font-bold text-neutral-100"
                        >
                            <span class="inline-block bg-accent-red px-2 py-1">
                                {{ t('product.sale') }}
                            </span>
                        </div>
                        <div
                            v-if="product.isNew"
                            class="text-xs font-bold text-neutral-100"
                        >
                            <span class="inline-block bg-brand-primary px-2 py-1">
                                {{ t('product.new') }}
                            </span>
                        </div>
                    </div>

                    <template v-if="cover.placeholder">
                        <SharedImagePlaceholder class="relative z-0" />
                    </template>

                    <template v-else>
                        <img
                            :src="cover.url"
                            :alt="cover.alt"
                            class="transition-duration-500 z-10 aspect-square w-full object-center transition-opacity ease-in-out group-hover:opacity-75"
                            :class="displayMode === 'standard' ? 'object-scale-down' : 'object-' + displayMode"
                        />
                    </template>
                </div>

                <div
                    v-if="layout !== 'minimal'"
                    class="flex justify-between pt-1.5"
                >
                    <!-- CUSTOM IDS-95: changed most of the class styling-->
                    <!-- CUSTOM IDS-134: add minimal styling of the card -->
                    <div class="flex max-w-full flex-col gap-px pb-4">
                        <!-- CUSTOM IDS-95: displaying product number -->
                        <h6 class="non-italic font-main text-sm leading-5 text-gray-medium">
                            {{ product.productNumber }}
                        </h6>
                        <h6 class="mb-3 line-clamp-2 h-10 font-main text-sm font-extrabold leading-5 text-neutral-800">
                            {{ getTranslatedProperty(product, 'name') }}
                        </h6>

                        <!-- CUSTOM IDS-95 adding figures for shank/size/figure placeholder for now. Will be worked on in another ticket -->
                        <div class="flex flex-wrap gap-2 pb-4">
                            <span
                                v-for="option in product.options"
                                :key="option.id"
                                class="inline-block rounded bg-gray-light px-2 py-[3px] font-main text-xs font-normal leading-[18px] text-neutral-800"
                            >
                                {{
                                    `${getTranslatedProperty(option.group, 'name')}: ${getTranslatedProperty(option, 'name')}`
                                }}
                            </span>
                        </div>
                        <span
                            v-if="moreVariantsLabel"
                            class="overflow-hidden text-ellipsis whitespace-nowrap text-sm underline"
                        >
                            {{ moreVariantsLabel }}
                        </span>
                        <h6
                            v-if="quantityAndPackage"
                            class="non-italic pt-4 font-main text-sm leading-5 text-neutral-800"
                        >
                            {{ quantityAndPackage }}
                        </h6>
                    </div>
                </div>
            </div>
        </LocaleLink>
        <!--        CUSTOM IDS-151: if product not available, don't show add to cart section-->
        <template v-if="isAddToCartAvailable">
            <!-- CUSTOM IDS-95: price and button aligned horizontally-->
            <div
                v-if="layout !== 'minimal'"
                class="flex items-center justify-between min-h-[60px]"
            >
                <!-- IDS-280 show custom template -->
                <ProductPrice :product="product"

                              msrp-text-class="text-sm mr-1"
                              msrp-price-class="text-lg font-extrabold">
                    <template #priceHidden>
                        <div class="text-sm">
                            <LocaleLink
                                :to="`/account/login?redirect=${route.fullPath}`"
                                class="font-bold underline"
                            >
                                {{ t('global.price.loginNeeded.register') }}
                            </LocaleLink>
                            {{ t('global.price.loginNeeded.forPrice') }}
                        </div>
                    </template>
                </ProductPrice>

                <ProductAddToCart
                    :product="product"
                    :label="false"
                    :icon="true"
                />
            </div>
            <!-- IDS-134 minimal layout of the card -->
            <div
                v-else
                class="flex justify-between gap-2 pt-2 min-h-[60px]"
            >
                <div>
                    <h6 class="line-clamp-1 pb-1 font-main font-bold leading-5 text-neutral-800">
                        {{ getTranslatedProperty(product, 'name') }}
                    </h6>
                    <!-- IDS-280 show custom template -->
                    <ProductPrice
                        :product="product"
                        font-weight="font-normal"
                        font-size="text-sm"
                        layout="minimal"
                        msrp-text-class="text-sm mr-1"
                        msrp-price-class="text-sm">
                    >
                        <template #priceHidden>
                            <div class="text-sm">
                                {{ t('global.price.loginNeeded.priceAfter') }}
                                <LocaleLink
                                    :to="`/account/login?redirect=${route.fullPath}`"
                                    class="underline"
                                >
                                    {{ t('global.price.loginNeeded.registration') }}
                                </LocaleLink>
                            </div>
                        </template>
                    </ProductPrice>
                </div>

                <div class="min-w-16">
                    <ProductAddToCart
                        :product="product"
                        :label="false"
                        :icon="true"
                    />
                </div>
            </div>
        </template>
        <template v-else>
            <div class="flex items-center min-h-[60px]">
                <p class="text-sm font-bold leading-5.5">{{ t('product.unavailable') }}</p>
            </div>
        </template>

        <template v-if="product.childCount > 0 && layout !== 'minimal'">
            <ProductGoToDetail :product="product" />
        </template>
    </div>
</template>
